/**
 * WebSocket client for the signaling server.
 *
 * The viewer page and the host UI share this. It only ever carries room
 * membership and SDP/ICE; once a peer connection is up, media and chat bypass
 * the server entirely.
 *
 * Reconnection is built in from the start because the failure it handles is not
 * exotic: a laptop changing Wi-Fi networks drops the socket while the media path
 * (a different socket pair) keeps working. The caller gets `onOpen` again after
 * a reconnect so it can re-send its join message.
 */

import { PROTOCOL_VERSION, type ClientMessage, type ServerMessage } from './protocol'
import { isDesktopApp, isMobileBuild } from './platform'

export type SignalingStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed'

export interface SignalingHandlers {
  /** Connection state changed. `detail` carries the reason for a close. */
  onStatus?: (status: SignalingStatus, detail?: string) => void
  /** A server message arrived. */
  onMessage?: (message: ServerMessage) => void
  /**
   * The connection is usable. Fires again after every reconnect, which is where
   * a caller re-sends `viewer_join`.
   */
  onOpen?: () => void
  /** A transport-level problem, already human-readable. */
  onError?: (message: string) => void
}

export interface SignalingOptions {
  handlers?: SignalingHandlers
  /** Reconnect automatically after an unexpected close. Defaults to true. */
  autoReconnect?: boolean
  /** Backoff ceiling. Defaults to 10 s. */
  maxBackoffMs?: number
}

/**
 * Where the signaling server lives.
 *
 * In a dev server it is the local axum process; in production the page is served
 * by the same Caddy instance that proxies `/ws`, so same-origin is correct there.
 */
export function defaultSignalUrl(): string {
  const override = import.meta.env.VITE_SIGNAL_URL
  if (override) return override
  if (isMobileBuild) throw new Error('The mobile signaling endpoint is not configured.')

  if (import.meta.env.DEV) return 'ws://127.0.0.1:8080/ws'
  // The desktop origin serves bundled assets, not the signaling service.
  if (isDesktopApp) return 'wss://app.lugiac.dedyn.io/ws'

  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${location.host}/ws`
}

interface PendingPing {
  sentAt: number
  resolve: (rttMs: number) => void
}

// P2P media does not keep the signaling socket busy. Tunnel/proxy idle
// timeouts can otherwise remove the viewer from the room and tear down a
// healthy media connection roughly every two minutes.
const HEARTBEAT_INTERVAL_MS = 20_000
const CONNECT_TIMEOUT_MS = 12_000

export class SignalingClient {
  readonly url: string

  #socket: WebSocket | null = null
  #handlers: SignalingHandlers
  #autoReconnect: boolean
  #maxBackoffMs: number
  #attempt = 0
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null
  #heartbeatTimer: ReturnType<typeof setInterval> | null = null
  #connectTimer: ReturnType<typeof setTimeout> | null = null
  #closedByUs = false
  #status: SignalingStatus = 'idle'
  #pendingPings = new Map<number, PendingPing>()
  #nextNonce = 1

  constructor(url: string, options: SignalingOptions = {}) {
    this.url = url
    this.#handlers = options.handlers ?? {}
    this.#autoReconnect = options.autoReconnect ?? true
    this.#maxBackoffMs = options.maxBackoffMs ?? 10_000
  }

  get status(): SignalingStatus {
    return this.#status
  }

  get isOpen(): boolean {
    return this.#socket?.readyState === WebSocket.OPEN
  }

  /** Open the connection. Safe to call when already connecting or open. */
  connect(): void {
    if (
      this.#socket &&
      (this.#socket.readyState === WebSocket.OPEN ||
        this.#socket.readyState === WebSocket.CONNECTING)
    ) {
      return
    }
    this.#closedByUs = false
    this.#setStatus(this.#attempt === 0 ? 'connecting' : 'reconnecting')

    let socket: WebSocket
    try {
      socket = new WebSocket(this.url)
    } catch (error) {
      // A malformed URL throws synchronously; there is nothing to reconnect to.
      this.#setStatus('closed', 'invalid URL')
      this.#handlers.onError?.(`could not open ${this.url}: ${String(error)}`)
      return
    }
    this.#socket = socket
    this.#connectTimer = setTimeout(() => {
      this.#connectTimer = null
      if (this.#socket !== socket) return
      this.#handlers.onError?.('connection timed out')
      socket.close(4000, 'connection timed out')
    }, CONNECT_TIMEOUT_MS)

    socket.onopen = () => {
      if (this.#socket !== socket) return
      this.#clearConnectTimer()
      this.#attempt = 0
      this.#startHeartbeat()
      this.#setStatus('open')
      this.#handlers.onOpen?.()
    }

    socket.onmessage = (event) => {
      if (this.#socket !== socket) return
      if (typeof event.data !== 'string') return
      let message: ServerMessage
      try {
        message = JSON.parse(event.data) as ServerMessage
      } catch {
        this.#handlers.onError?.('the server sent a message that is not valid JSON')
        return
      }
      if (message.type === 'pong') {
        this.#settlePing(message.nonce)
        return
      }
      this.#handlers.onMessage?.(message)
    }

    socket.onerror = () => {
      if (this.#socket !== socket) return
      // The browser gives no detail here on purpose; `onclose` carries the code.
      this.#handlers.onError?.(`signaling connection error (${this.url})`)
    }

    socket.onclose = (event) => {
      if (this.#socket !== socket) return
      this.#clearConnectTimer()
      this.#stopHeartbeat()
      this.#socket = null
      this.#failPendingPings()

      if (this.#closedByUs) {
        this.#setStatus('closed')
        return
      }

      const detail = event.reason || `code ${event.code}`
      if (this.#autoReconnect) {
        this.#setStatus('reconnecting', detail)
        this.#scheduleReconnect()
      } else {
        this.#setStatus('closed', detail)
      }
    }
  }

  /** Send a message. Returns false when the socket is not open. */
  send(message: ClientMessage): boolean {
    if (!this.isOpen) return false
    this.#socket!.send(JSON.stringify(message))
    return true
  }

  /**
   * Measure round-trip time to the signaling server.
   *
   * Resolves with milliseconds, or rejects after `timeoutMs` (default 5 s) so a
   * caller can show "no response" instead of hanging forever.
   */
  ping(timeoutMs = 5_000): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      if (!this.isOpen) {
        reject(new Error('not connected'))
        return
      }
      const nonce = this.#nextNonce++
      const timer = setTimeout(() => {
        this.#pendingPings.delete(nonce)
        reject(new Error('no pong within the timeout'))
      }, timeoutMs)

      this.#pendingPings.set(nonce, {
        sentAt: performance.now(),
        resolve: (rtt) => {
          clearTimeout(timer)
          resolve(rtt)
        },
      })

      this.send({ type: 'ping', nonce })
    })
  }

  /** Close deliberately. Disables reconnect. */
  close(): void {
    this.#clearConnectTimer()
    this.#closedByUs = true
    this.#clearReconnectTimer()
    this.#stopHeartbeat()
    this.#failPendingPings()
    this.#socket?.close(1000, 'client closing')
    this.#socket = null
    this.#setStatus('closed')
  }

  /** Convenience: build the `viewer_join` message this client should send. */
  joinMessage(code: string, name: string, password?: string): ClientMessage {
    return {
      type: 'viewer_join',
      code,
      ...(password ? { password } : {}),
      name,
      protocol_version: PROTOCOL_VERSION,
    }
  }

  #setStatus(status: SignalingStatus, detail?: string): void {
    if (this.#status === status && !detail) return
    this.#status = status
    this.#handlers.onStatus?.(status, detail)
  }

  #clearConnectTimer(): void {
    if (this.#connectTimer !== null) clearTimeout(this.#connectTimer)
    this.#connectTimer = null
  }

  #startHeartbeat(): void {
    this.#stopHeartbeat()
    this.#heartbeatTimer = setInterval(() => {
      // The server already answers application pings. Heartbeat pongs need no
      // RTT promise; the normal message handler consumes them.
      this.send({ type: 'ping', nonce: this.#nextNonce++ })
    }, HEARTBEAT_INTERVAL_MS)
  }

  #stopHeartbeat(): void {
    if (this.#heartbeatTimer) clearInterval(this.#heartbeatTimer)
    this.#heartbeatTimer = null
  }

  #scheduleReconnect(): void {
    this.#clearReconnectTimer()
    // 500 ms, 1 s, 2 s, 4 s, 8 s, then the ceiling. Jitter avoids a roomful of
    // viewers reconnecting in lockstep after a server restart.
    const base = Math.min(500 * 2 ** this.#attempt, this.#maxBackoffMs)
    const delay = base * (0.5 + Math.random() * 0.5)
    this.#attempt++

    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null
      this.connect()
    }, delay)
  }

  #clearReconnectTimer(): void {
    if (this.#reconnectTimer !== null) {
      clearTimeout(this.#reconnectTimer)
      this.#reconnectTimer = null
    }
  }

  #settlePing(nonce: number): void {
    const pending = this.#pendingPings.get(nonce)
    if (!pending) return
    this.#pendingPings.delete(nonce)
    pending.resolve(performance.now() - pending.sentAt)
  }

  #failPendingPings(): void {
    this.#pendingPings.clear()
  }
}
